import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Grid,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import { Add, Delete, Edit, Person } from "@mui/icons-material";
import dayjs from "dayjs";
import PopupModal from "../Ui_elements/PopupModal";
import DatePickerComponent from "../DatePickerComponent";
import TextFieldComponent from "../TextFieldComponent";
import TimePickerComponent from "../TimePickerComponent";
import { formatDisplayTime } from "./dateFormat";
import { getDateRangeError, getTimeRangeError } from "./availabilityValidation";
import { STATUS_OPTIONS, STATUSES_WITH_HOURS, getHoursFieldLabels } from "./statusConfig";

const RangeModal = ({
  open,
  onClose,
  isFreelancer,
  loginUserId,
  resource,
  onSave,
  onDelete,
  editingRecord,
}) => {
  const [selectedLinguist, setSelectedLinguist] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");
  const [notes, setNotes] = useState("");
  const [rangeStart, setRangeStart] = useState(null);
  const [rangeEnd, setRangeEnd] = useState(null);
  const [timeFrom, setTimeFrom] = useState("");
  const [timeTo, setTimeTo] = useState("");
  const [dateToError, setDateToError] = useState("");
  const [timeToError, setTimeToError] = useState("");

  useEffect(() => {
    if (open && editingRecord) {
      setSelectedLinguist(editingRecord.user_id || "");
      setSelectedStatus(editingRecord.eStatus || "");
      setNotes(editingRecord.vNotes || "");
      setRangeStart(dayjs(editingRecord.date_from));
      setRangeEnd(dayjs(editingRecord.date_to));
      setTimeFrom(formatDisplayTime(editingRecord.time_from));
      setTimeTo(formatDisplayTime(editingRecord.time_to));
    } else if (open) {
      setSelectedLinguist("");
      setSelectedStatus("");
      setNotes("");
      setRangeStart(dayjs());
      setRangeEnd(dayjs());
      setTimeFrom("");
      setTimeTo("");
    }
    if (open) {
      setDateToError("");
      setTimeToError("");
    }
  }, [open, editingRecord]);

  const showHours = STATUSES_WITH_HOURS.includes(selectedStatus);
  const hourLabels = getHoursFieldLabels(selectedStatus);

  const handleRangeStartChange = e => {
    const nextStart = dayjs(e.target.value);
    setRangeStart(nextStart);
    setDateToError(
      getDateRangeError(nextStart.format("YYYY-MM-DD"), rangeEnd?.format("YYYY-MM-DD"))
    );
  };

  const handleRangeEndChange = e => {
    const nextEnd = dayjs(e.target.value);
    setRangeEnd(nextEnd);
    setDateToError(
      getDateRangeError(rangeStart?.format("YYYY-MM-DD"), nextEnd.format("YYYY-MM-DD"))
    );
  };

  const handleTimeFromChange = e => {
    const nextFrom = e.target.value;
    setTimeFrom(nextFrom);
    setTimeToError(getTimeRangeError(nextFrom, timeTo));
  };

  const handleTimeToChange = e => {
    const nextTo = e.target.value;
    setTimeTo(nextTo);
    setTimeToError(getTimeRangeError(timeFrom, nextTo));
  };

  const handleSave = () => {
    if (!selectedStatus || !rangeStart || !rangeEnd) return;

    const nextDateToError = getDateRangeError(
      dayjs(rangeStart).format("YYYY-MM-DD"),
      dayjs(rangeEnd).format("YYYY-MM-DD")
    );
    const nextTimeToError = showHours ? getTimeRangeError(timeFrom, timeTo) : "";
    setDateToError(nextDateToError);
    setTimeToError(nextTimeToError);
    if (nextDateToError || nextTimeToError) return;

    onSave({
      user_id: isFreelancer ? loginUserId : selectedLinguist,
      date_from: dayjs(rangeStart).format("YYYY-MM-DD"),
      date_to: dayjs(rangeEnd).format("YYYY-MM-DD"),
      eStatus: selectedStatus,
      time_from: showHours && timeFrom ? timeFrom : null,
      time_to: showHours && timeTo ? timeTo : null,
      vNotes: notes,
      languages: [],
    });
  };

  return (
    <PopupModal
      open={open}
      handleClose={onClose}
      title={editingRecord ? "Edit Availability Range" : "Add Availability Range"}
      maxWidth="md"
      fullWidth
      width="650px"
    >
      <Box sx={{ p: 3 }}>
        <Grid container spacing={2}>
          {!isFreelancer && (
            <Grid item xs={6}>
              <FormControl fullWidth>
                <FormLabel>Linguist</FormLabel>
                <Select
                  value={selectedLinguist}
                  onChange={e => setSelectedLinguist(e.target.value)}
                  startAdornment={<Person sx={{ mr: 1, color: "text.secondary" }} />}
                >
                  <MenuItem value="">Select Linguist</MenuItem>
                  {resource.map(ling => (
                    <MenuItem key={ling.iUserId} value={ling.iUserId}>
                      {ling.vUserName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}

          <Grid item xs={isFreelancer ? 12 : 6}>
            <FormControl fullWidth>
              <FormLabel>Status</FormLabel>
              <Select value={selectedStatus} onChange={e => setSelectedStatus(e.target.value)}>
                <MenuItem value="">Select Status</MenuItem>
                {STATUS_OPTIONS.map(opt => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.short} — {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {showHours && (
            <>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">
                  {hourLabels.section}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <TimePickerComponent
                  name="time_from"
                  label={hourLabels.from}
                  value={timeFrom}
                  onChange={handleTimeFromChange}
                />
              </Grid>
              <Grid item xs={6}>
                <TimePickerComponent
                  name="time_to"
                  label={hourLabels.to}
                  value={timeTo}
                  onChange={handleTimeToChange}
                  error={timeToError}
                />
              </Grid>
            </>
          )}

          <Grid item xs={12} sm={6}>
            <DatePickerComponent
              label="From Date"
              value={rangeStart ? dayjs(rangeStart).format("YYYY-MM-DD") : ""}
              onChange={handleRangeStartChange}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <DatePickerComponent
              label="To Date"
              value={rangeEnd ? dayjs(rangeEnd).format("YYYY-MM-DD") : ""}
              onChange={handleRangeEndChange}
              error={dateToError}
            />
          </Grid>

          <Grid item xs={12}>
            <TextFieldComponent
              name="notes"
              label="Notes"
              type="text"
              value={notes}
              onChange={e => setNotes(e.target.value)}
              isNotMandatory
              multiline
              minRows={3}
              placeHolder="Add notes..."
            />
          </Grid>
        </Grid>

        <Box sx={{ display: "flex", justifyContent: "space-between", mt: 3 }}>
          {editingRecord && (
            <Button color="error" onClick={onDelete} startIcon={<Delete />}>
              Delete
            </Button>
          )}
          <Box sx={{ display: "flex", gap: 1, ml: "auto" }}>
            <Button onClick={onClose} variant="outlined">
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={!selectedStatus || !rangeStart || !rangeEnd}
              startIcon={editingRecord ? <Edit /> : <Add />}
            >
              {editingRecord ? "Update" : "Save"}
            </Button>
          </Box>
        </Box>
      </Box>
    </PopupModal>
  );
};

export default RangeModal;
